import { Box, BoxProps, Stack } from "@mui/material";
import React, { ReactNode } from "react";
import SearchInput from "../../../../SearchInput";
import StyledButtonV2 from "../../../StyledButtonV2";
import { CHART_COLORS, CHART_DIMENSIONS, CHART_TYPOGRAPHY } from "./tokens";

export interface TertiaryTab {
  value: string;
  label: string;
}

interface TertiaryNavbarProps {
  tabs: TertiaryTab[];
  activeTab: string;
  onTabChange: (value: string) => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  showSearch?: boolean;
  actionLabel?: string;
  actionIcon?: ReactNode;
  onAction?: () => void;
  endContent?: ReactNode;
  sx?: BoxProps["sx"];
}

const TertiaryNavbar: React.FC<TertiaryNavbarProps> = ({
  tabs,
  activeTab,
  onTabChange,
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "Search...",
  showSearch = true,
  actionLabel,
  actionIcon,
  onAction,
  endContent,
  sx,
}) => {
  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      sx={{
        bgcolor: CHART_COLORS.bgSecondaryBlue,
        borderBottom: `1px solid ${CHART_COLORS.border}`,
        height: CHART_DIMENSIONS.tertiaryNavHeight,
        px: CHART_DIMENSIONS.cardHeaderPx,
        gap: "4px",
        ...sx,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          flex: 1,
          overflowX: "auto",
          gap: "12px",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {tabs.map((tab) => {
          const isActive = tab.value === activeTab;
          return (
            <Box
              key={tab.value}
              onClick={() => onTabChange(tab.value)}
              sx={{
                cursor: "pointer",
                px: "4px",
                pb: "8px",
                pt: "8px",
                borderBottom: isActive
                  ? `2px solid ${CHART_COLORS.primary}`
                  : "2px solid transparent",
                fontFamily: CHART_TYPOGRAPHY.fontFamily,
                ...CHART_TYPOGRAPHY.body5,
                fontWeight: isActive
                  ? CHART_TYPOGRAPHY.weight.medium
                  : CHART_TYPOGRAPHY.weight.regular,
                color: isActive
                  ? CHART_COLORS.primaryPressed
                  : CHART_COLORS.textDark,
                whiteSpace: "nowrap",
                userSelect: "none",
                transition: "border-color 0.15s ease, color 0.15s ease",
                "&:hover": {
                  color: CHART_COLORS.primaryPressed,
                  borderBottomColor: isActive
                    ? CHART_COLORS.primary
                    : "rgba(16, 78, 132, 0.35)",
                },
              }}
            >
              {tab.label}
            </Box>
          );
        })}
      </Stack>

      {(showSearch || actionLabel || endContent) && (
        <Stack direction="row" alignItems="center" gap="6px" flexShrink={0}>
          {showSearch && (
            <Box width={CHART_DIMENSIONS.searchWidth}>
              <SearchInput
                searchKey={searchValue}
                onSearch={(v) => onSearchChange?.(v)}
                placeholder={searchPlaceholder}
              />
            </Box>
          )}

          {actionLabel && (
            <StyledButtonV2
              onClick={onAction}
              startIcon={actionIcon}
              disableElevation
              label={actionLabel}
            />
          )}
          {endContent}
        </Stack>
      )}
    </Box>
  );
};

export default TertiaryNavbar;
